import React from 'react';
import { DefaultButton, Icon, IconButton, Link as FluentLink, Text } from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { useGetIntegrationHealthQuery } from '../../store/endpoints/IntegrationHealthEndpoints';
import {
    useSimpleValidateExtensionFolioMutation,
    useSimpleValidateInstalledExtensionMutation
} from '../../store/Api';
import {
    IIntegrationHealthItem, IIntegrationSyncFailure, IntegrationHealthStatus, IWebhookHealthWarning
} from '../../models/IntegrationHealth';
import { buildHealthRows } from './integrationHealthRows';
import { ConvertToTimeAgo } from '../../logic/DateHelpers';
import { HomeListMore, useHomeListPreview } from './HomeListMore';

interface IIntegrationHealthListProps {
    useDemoData?: boolean;
}

const DEMO_INTEGRATIONS: IIntegrationHealthItem[] = [
    {
        installedId: 'demo-cwpsa',
        sourceId: 'demo-cwpsa',
        name: 'ConnectWise PSA',
        isFolio: true,
        status: 'healthy',
        lastValidated: new Date(new Date().getTime() - 60 * 60 * 1000),
        lastSuccessfulSync: new Date(new Date().getTime() - 5 * 60 * 60 * 1000),
    },
    {
        installedId: 'demo-autotask',
        sourceId: 'demo-autotask',
        name: 'Autotask',
        isFolio: true,
        status: 'healthy',
        lastValidated: new Date(new Date().getTime() - 2 * 60 * 60 * 1000),
        syncFailures: [{
            at: new Date(new Date().getTime() - 30 * 60 * 1000),
            capabilityName: 'Product Import',
            message: 'Import failed during Fetch',
            error: 'The logged in Resource does not have the adequate permissions to query this entity type.',
        }],
    },
    {
        installedId: 'demo-ingram',
        sourceId: 'demo-ingram',
        name: 'Ingram Micro',
        isFolio: false,
        status: 'failing',
        lastValidated: new Date(new Date().getTime() - 60 * 60 * 1000),
        message: 'The provided credentials were rejected',
    },
];

const STATUS_ICONS: Record<IntegrationHealthStatus, { iconName: string, color: string }> = {
    healthy: { iconName: 'SkypeCircleCheck', color: '#107c10' },
    failing: { iconName: 'StatusErrorFull', color: '#a4262c' },
    unverifiable: { iconName: 'Blocked2', color: '#605e5c' },
    unknown: { iconName: 'CircleRing', color: '#605e5c' },
};

const IntegrationHealthList: React.FC<IIntegrationHealthListProps> = (props) => {
    const health = useGetIntegrationHealthQuery();
    const history = useHistory();

    const realItems = health.data?.integrations ?? [];
    const webhookWarnings = health.data?.webhookWarnings ?? [];
    const usingDemoData = !!props.useDemoData && realItems.length === 0 && webhookWarnings.length === 0;
    const items = usingDemoData ? DEMO_INTEGRATIONS : realItems;

    // Sorted before the preview is taken, so the rows worth acting on are the ones that survive the cap.
    const rows = buildHealthRows(items, webhookWarnings);
    const preview = useHomeListPreview(rows);

    // An environment where the endpoint is not deployed yet simply shows no widget rather than an error.
    if (health.isError) return null;
    if (health.isLoading) return null;

    if (rows.length === 0) {
        return (
            <div className='home-list fade-in integration-health-list'>
                <h3>Integration Health</h3>
                <Text block>
                    No integrations connected yet.{' '}
                    <FluentLink onClick={() => history.push('/settings/extensions')}>Connect one</FluentLink>
                    {' '}to keep your catalog and pricing in sync.
                </Text>
            </div>
        );
    }

    return (
        <div className='home-list fade-in integration-health-list'>
            <h3>Integration Health</h3>
            {preview.visible.map((row) => row.kind === 'webhook'
                ? <WebhookWarningRow warning={row.warning} key={row.key} />
                : <IntegrationHealthRow item={row.item} isDemo={usingDemoData} key={row.key} />)}
            <HomeListMore hiddenCount={preview.hiddenCount} expanded={preview.expanded} onToggle={preview.toggle} />
        </div>
    );
}

const IntegrationHealthRow: React.FC<{ item: IIntegrationHealthItem, isDemo?: boolean }> = ({ item, isDemo }) => {
    const history = useHistory();
    const [validateExtension, extensionValidation] = useSimpleValidateInstalledExtensionMutation();
    const [validateFolio, folioValidation] = useSimpleValidateExtensionFolioMutation();
    const validating = extensionValidation.isLoading || folioValidation.isLoading;

    const icon = STATUS_ICONS[item.status] ?? STATUS_ICONS.unknown;
    const displayName = item.nickname || item.name || item.sourceId;

    // The panel resolves ?instance= against the installed id; 'edit' is the credential tab on both panels and
    // 'logs' the Activity tab, where a failed import's full record is.
    const panelBase = `/settings/extensions?panel=${item.isFolio ? 'extensionFolio' : 'extension'}&instance=${encodeURIComponent(item.installedId)}`;
    const panelLink = `${panelBase}&panelTab=edit`;
    const activityLink = `${panelBase}&panelTab=logs`;
    const syncFailures = item.syncFailures ?? [];
    const openPanel = () => {
        if (isDemo) return;
        history.push(panelLink);
    };

    // Folio validation is keyed by the folio's source id, standalone by the installed id — the same ids
    // the validate buttons on the extension-management table use. The mutations invalidate the health
    // tag, so the row re-renders with the fresh stamp.
    const recheck = () => {
        if (isDemo || validating) return;
        if (item.isFolio) validateFolio({ id: item.sourceId });
        else validateExtension({ id: item.installedId });
    };

    const subline = item.status === 'failing'
        ? (item.message || 'The last credential check failed')
        : item.status === 'unverifiable'
            ? 'Cannot be verified automatically'
            : item.status === 'unknown'
                ? 'Not yet checked'
                : item.lastSuccessfulSync
                    ? `Last synced ${ConvertToTimeAgo(new Date(item.lastSuccessfulSync))}`
                    : item.lastValidated
                        ? `Checked ${ConvertToTimeAgo(new Date(item.lastValidated))}`
                        : undefined;

    // The whole row is a quiet link to the extension's own panel — healthy rows included, since "open
    // and look" shouldn't require a broken state. The inner buttons stop propagation so a re-check or
    // the failing CTA never also navigates.
    return (
        <div className='integration-health-row' role='link' tabIndex={isDemo ? undefined : 0}
            title={`Open ${displayName} settings`}
            onClick={openPanel}
            onKeyDown={(e) => { if (e.key === 'Enter') openPanel(); }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6em', padding: '0.4em 0.25em' }}>
            <Icon iconName={icon.iconName} style={{ color: icon.color, fontSize: 16, marginTop: 2 }} />
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Text block nowrap title={displayName}>{displayName}</Text>
                {subline &&
                    <Text block variant='small' style={item.status === 'failing' ? { color: '#a4262c' } : { color: '#605e5c' }}>
                        {subline}
                    </Text>}
                {(item.staleOptionValues?.length ?? 0) > 0 &&
                    <Text block variant='small' style={{ color: '#8a6116' }}
                        title={`No longer valid: ${item.staleOptionValues!.join(', ')}`}>
                        ⚠ {item.staleOptionValues!.length} saved setting{item.staleOptionValues!.length === 1 ? '' : 's'} no longer valid
                    </Text>}
                {syncFailures.slice(0, MAX_SYNC_FAILURES_SHOWN).map((failure) =>
                    <SyncFailureLine failure={failure} key={failure.capability ?? ''} />)}
                {syncFailures.length > MAX_SYNC_FAILURES_SHOWN &&
                    <Text block variant='small' style={{ color: WARNING_COLOR }}>
                        and {syncFailures.length - MAX_SYNC_FAILURES_SHOWN} more
                    </Text>}
                {item.status !== 'failing' && syncFailures.length > 0 && !isDemo &&
                    <DefaultButton
                        text='View activity'
                        styles={{ root: { marginTop: '0.35em', height: 28 } }}
                        onClick={(e) => { e.stopPropagation(); history.push(activityLink); }}
                    />}
                {item.status === 'failing' &&
                    <DefaultButton
                        text='Fix credentials'
                        styles={{ root: { marginTop: '0.35em', height: 28 } }}
                        onClick={(e) => { e.stopPropagation(); history.push(panelLink); }}
                    />}
            </div>
            {item.status !== 'unverifiable' &&
                <IconButton
                    iconProps={{ iconName: validating ? 'ProgressRingDots' : 'Refresh' }}
                    title='Check now'
                    ariaLabel={`Check ${displayName} now`}
                    disabled={validating}
                    onClick={(e) => { e.stopPropagation(); recheck(); }}
                />}
        </div>
    );
}

const WARNING_COLOR = '#8a6116';
const MAX_SYNC_FAILURES_SHOWN = 2;

/**
 * One capability's failed import or sync, in the connected system's own words (e.g. keys deactivated, a role without
 * permission), since that is what tells the person what to fix. Long errors are clamped; the title has the whole text.
 */
const SyncFailureLine: React.FC<{ failure: IIntegrationSyncFailure }> = ({ failure }) => {
    const what = failure.capabilityName || 'Import';
    const detail = failure.error || failure.message || 'No details were reported';
    return (
        <Text block variant='small' title={detail}
            style={{
                color: WARNING_COLOR, overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            }}>
            ⚠ {what} failed {ConvertToTimeAgo(new Date(failure.at))}: {detail}
        </Text>
    );
}

/**
 * A webhook whose last call failed. The row and its button open the webhook on its Logs tab, where the failing
 * calls are; there's nothing to re-check from here, since only the webhook's next real call can show it working.
 */
const WebhookWarningRow: React.FC<{ warning: IWebhookHealthWarning }> = ({ warning }) => {
    const history = useHistory();
    const displayName = warning.name || 'Webhook';
    const link = `/settings/webhooks?panel=webhook&instance=${encodeURIComponent(warning.webhookId)}&panelTab=logs`;
    const open = () => history.push(link);

    const failures = warning.recentFailures === 1 ? '1 failed call' : `${warning.recentFailures} failed calls`;
    const lastWorked = warning.lastSuccess
        ? `last worked ${ConvertToTimeAgo(new Date(warning.lastSuccess))}`
        : 'no successful call this week';

    return (
        <div className='integration-health-row' role='link' tabIndex={0}
            title={`Open the ${displayName} webhook`}
            onClick={open}
            onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6em', padding: '0.4em 0.25em' }}>
            <Icon iconName='Warning' style={{ color: WARNING_COLOR, fontSize: 16, marginTop: 2 }} />
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Text block nowrap title={displayName}>{displayName} <Text variant='small' style={{ color: '#605e5c' }}>(webhook)</Text></Text>
                <Text block variant='small' style={{ color: WARNING_COLOR }}>{warning.message}</Text>
                <Text block variant='small' style={{ color: '#605e5c' }}>
                    Failed {ConvertToTimeAgo(new Date(warning.lastFailure))}; {failures} this week, {lastWorked}
                </Text>
                <DefaultButton
                    text='Review webhook'
                    styles={{ root: { marginTop: '0.35em', height: 28 } }}
                    onClick={(e) => { e.stopPropagation(); open(); }}
                />
            </div>
        </div>
    );
}

export default IntegrationHealthList;
